import { consume } from '@lit/context';
import { gsap } from 'gsap';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';

import { gameApiContext } from '../../context';
import type { GameAPIType } from '../../GameAPI';
import { tokens } from '../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../UIState';
import { bindValtio } from '../../ValtioController';

/**
 * Invite Panel - Share game invite link with friends
 *
 * Modal panel for sharing the current game with friends.
 * Displays invite link and provides copy-to-clipboard functionality.
 *
 * Features:
 * - Displays generated invite link
 * - Copy button with "Copied!" feedback; a click on the link copies it too
 * - Right-click or long-press on the link for the browser's own link menu
 * - GSAP entrance/exit animations
 * - ESC or backdrop click to close
 *
 * @element invite-panel
 */
@customElement('invite-panel')
export class InvitePanel extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        /* Declared here rather than inherited from the body: the panel owns
           its type, and a tap to close it shouldn't start a text selection,
           callout or a grey flash across the whole screen. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      .backdrop {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--color-bg-backdrop);
        z-index: 200;
        /* Drops the 300ms double-tap delay from tap-to-close. */
        touch-action: manipulation;
      }

      .panel {
        position: relative;
        box-sizing: border-box;
        width: 400px;
        max-width: calc(100vw - 32px);
        background-color: var(--color-bg-modal);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        cursor: default;
        overflow: hidden;
      }

      .header {
        padding: 16px 24px;
        border-bottom: 1px solid var(--color-border-soft);
        font-size: var(--font-size-lg);
        font-weight: bold;
        color: var(--color-text-bright);
      }

      .content {
        padding: 16px 24px 24px;
      }

      .desc {
        font-size: var(--font-size-sm);
        color: var(--color-text-tooltip);
        line-height: 1.5;
        margin-bottom: 16px;
      }

      .link-container {
        display: flex;
        /* Buttons wrap below the link when the panel narrows on phones */
        flex-wrap: wrap;
        gap: 8px;
        align-items: stretch;
      }

      .link {
        flex: 1 1 100%;
        padding: 10px 12px;
        background-color: var(--color-bg-input);
        border-radius: var(--radius-sm);
        font-size: var(--font-size-xs);
        color: var(--color-text-bright);
        word-break: break-all;
        line-height: 1.4;
        cursor: pointer;
        text-decoration: none;
        display: block;
        /* A long press on iOS gets the system's link menu (Copy Link, Share),
           the fallback should the clipboard refuse our copy. */
        -webkit-touch-callout: default;
      }

      @media (hover: hover) {
        .link:hover {
          background-color: var(--color-bg-item-hover);
        }
      }

      /* The raised button legacy's COPY had, as logon-panel's and
         login-selector's still are: a darker bottom edge that moves to the
         top while pressed. A button doesn't inherit its font, hence the
         font-family (it was Arial). */
      .action-btn {
        flex: 1;
        box-sizing: border-box;
        padding: 10px 16px;
        background-color: var(--color-btn-login);
        color: var(--color-text-white);
        text-shadow: var(--shadow-text-btn);
        border: none;
        border-bottom: 2px solid var(--color-border-soft);
        border-radius: var(--radius-sm);
        font-family: inherit;
        font-size: var(--font-size-sm);
        font-weight: bold;
        cursor: pointer;
        transition: background-color 0.15s;
        white-space: nowrap;
        min-width: 80px;
      }

      .action-btn:active {
        border-bottom: none;
        border-top: 2px solid var(--color-border-strong);
      }

      @media (hover: hover) {
        .action-btn:hover {
          background-color: var(--color-btn-login-hover);
          box-shadow: var(--shadow-btn-hover-soft);
        }
      }

      @media (hover: none) {
        .action-btn {
          min-height: 44px;
        }
      }

      .action-btn.copied {
        background-color: var(--color-btn-play);
      }

      .action-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** GameAPI instance for panel control */
  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  /** Reference to the panel element for GSAP animations */
  @query('.panel') private panel?: HTMLElement;

  /** Reference to the backdrop element for GSAP animations */
  @query('.backdrop') private backdrop?: HTMLElement;

  /** Guard against re-entrant close calls */
  private isClosing = false;

  /** Whether "Copied!" feedback is showing */
  @state() private showCopied = false;

  /** Timeout for resetting copied state */
  private copiedTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Whether the platform offers a native share sheet (mobile, macOS Safari).
   *  Captured once — capability doesn't change over the session. */
  private readonly canShare = typeof navigator !== 'undefined' && 'share' in navigator;

  /** @override Registers keyboard listener */
  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.handleKeyDown, { capture: true });
  }

  /** @override Cleans up keyboard listener, timers, and GSAP animations on disconnect */
  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleKeyDown, { capture: true });

    // Clean up copied timeout
    if (this.copiedTimeout) {
      clearTimeout(this.copiedTimeout);
      this.copiedTimeout = null;
    }

    // Clean up GSAP animations
    if (this.panel) gsap.killTweensOf(this.panel);
    if (this.backdrop) gsap.killTweensOf(this.backdrop);
  }

  /** @override Triggers entrance animation. Guard, don't reset: a close that
   *  lands before first render must not be undone into a ghost entrance. */
  override firstUpdated() {
    if (this.isClosing) return;
    this.animateIn();
  }

  /** Animates the panel and backdrop into view */
  private animateIn() {
    if (!this.panel || !this.backdrop) return;

    // Kill any existing tweens to prevent overlap
    gsap.killTweensOf(this.backdrop);
    gsap.killTweensOf(this.panel);

    gsap.fromTo(this.backdrop, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.out' });
    gsap.fromTo(this.panel, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out' });
  }

  /**
   * Animates the panel and backdrop out of view
   * @param onComplete - Callback invoked after animation completes
   */
  private animateOut(onComplete: () => void) {
    if (!this.panel || !this.backdrop) {
      onComplete();
      return;
    }

    // Kill any existing tweens to prevent overlap/double callbacks
    gsap.killTweensOf(this.backdrop);
    gsap.killTweensOf(this.panel);

    gsap.to(this.backdrop, { opacity: 0, duration: 0.3, ease: 'power2.in' });
    gsap.to(this.panel, { opacity: 0, scale: 0.9, duration: 0.3, ease: 'power2.in', onComplete });
  }

  /** Handles Escape key to close the panel. Consumed (capture phase) — the
   *  open modal owns its dismissal; the game-side Escape path is redundant. */
  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    this.close();
  };

  /** Handles backdrop click to close the panel */
  private handleBackdropClick = (e: Event) => {
    e.stopPropagation();
    this.close();
  };

  private stopClickPropagation = (e: Event) => {
    e.stopPropagation();
  };

  /** Animates out and closes the panel via GameAPI */
  private close() {
    if (this.isClosing) return;
    this.isClosing = true;
    this.animateOut(() => this.api.closePanel());
  }

  /** Handles copy button click. "Copied!" only shows when the clipboard
   *  write actually succeeded — on refusal the link text stays selectable
   *  as the manual fallback. */
  private handleCopy = async () => {
    this.api.playClick();
    const copied = await this.api.copyInviteLink();
    // Closing/closed panel: don't flash feedback or arm a timer that
    // disconnectedCallback has already missed its chance to clear.
    if (!copied || this.isClosing || !this.isConnected) return;
    this.showCopied = true;

    // Clear any existing timeout to prevent stacking
    if (this.copiedTimeout) clearTimeout(this.copiedTimeout);
    this.copiedTimeout = setTimeout(() => {
      this.showCopied = false;
      this.copiedTimeout = null;
    }, 2000);
  };

  /** A plain click on the link copies it, rather than opening the game again
   *  in a new tab. Modified clicks, and the middle button (which fires no
   *  click), keep the browser's own behaviour. */
  private handleLinkClick = (e: MouseEvent) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    void this.handleCopy();
  };

  /** The game cancels every right-click (UI.ts); the link lets the browser's
   *  menu through, for Copy link address, as legacy did. On Android a long
   *  press is a right-click too. */
  private allowLinkMenu = (e: MouseEvent) => {
    e.stopPropagation();
  };

  /** Opens the platform share sheet (mobile / macOS Safari) with the link. */
  private handleShare = async () => {
    this.api.playClick();
    const url = this.uiState.inviteLink;
    if (!url) return;
    try {
      await navigator.share({ title: 'Airmash', text: 'Join my Airmash game!', url });
    } catch {
      // User dismissed the share sheet — not an error.
    }
  };

  /** @override Renders the modal panel */
  override render() {
    const inviteLink = this.uiState.inviteLink || '';

    return html`
      <div class="backdrop" @click=${this.handleBackdropClick}>
        <div class="panel" @click=${this.stopClickPropagation}>
          <div class="header">Invite Friends</div>
          <div class="content">
            <div class="desc">
              Copy the link below, give it to someone else and they will be able to join the same game as you.
            </div>
            <div class="link-container">
              ${
                inviteLink
                  ? html`<a
                      class="link"
                      href=${inviteLink}
                      target="_blank"
                      rel="noopener"
                      @click=${this.handleLinkClick}
                      @contextmenu=${this.allowLinkMenu}
                      >${inviteLink}</a
                    >`
                  : html`<div class="link">Generating link...</div>`
              }
              <button
                class="action-btn ${this.showCopied ? 'copied' : ''}"
                ?disabled=${!inviteLink}
                @click=${this.handleCopy}
              >
                ${this.showCopied ? 'Copied!' : 'COPY'}
              </button>
              ${
                this.canShare
                  ? html`
                      <button class="action-btn" ?disabled=${!inviteLink} @click=${this.handleShare}>SHARE</button>
                    `
                  : nothing
              }
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'invite-panel': InvitePanel;
  }
}
